import Routes from "./Routes";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "moment/locale/es";
import "flag-icons";

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "react-tooltip/dist/react-tooltip.css";
import "@react-pdf-viewer/core/lib/styles/index.css";

import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { rgbaObjectToString } from "./helpers/utils";
import {
  PAGINATION_PAGE_SIZES,
  TOAST_BG_COLOR,
  TOAST_TEXT_COLOR,
} from "./helpers/constants";
import { useEffect, useLayoutEffect } from "react";
import useDarkMode from "./hooks/useDarkMode";
import { BrowserRouter as Router } from "react-router-dom";
import { provideGlobalGridOptions, themeMaterial } from "ag-grid-community";
import CellWithBackgroundRenderer from "./components/CustomCells/Renderers/CellWithBackgroundRenderer";
import { AG_GRID_LOCALE_ES } from "@ag-grid-community/locale";
import LayoutBackgroundFloatingIcons from "./layouts/TwoSidebars/LayoutBackgroundFloatingIcons";
import clsx from "clsx";
import useMobile from "./hooks/useMobile";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

provideGlobalGridOptions({
  theme: themeMaterial,
  localeText: {
    ...AG_GRID_LOCALE_ES,
    pageSizeSelectorLabel: "Tamaño de página:",
  },
  defaultColDef: {
    suppressMovable: true,
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    suppressFloatingFilterButton: true,
    suppressFiltersToolPanel: true,
    sortable: true,
    resizable: true,
    filter: false,
    floatingFilter: false,
  },
  // alwaysShowHorizontalScroll: true,
  rowModelType: "clientSide",
  domLayout: "autoHeight",
  rowSelection: {
    mode: "multiRow",
  },
  animateRows: false,
  components: { CellWithBackgroundRenderer },
  pagination: true,
  suppressPaginationPanel: false,
  suppressMoveWhenColumnDragging: true,
  suppressRowVirtualisation: false,
  selectionColumnDef: {
    sortable: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    pinned: true,
    width: 70,
    maxWidth: 70,
    minWidth: 70,
    lockPosition: "left",
  },
  paginationPageSizeSelector: PAGINATION_PAGE_SIZES,
  ensureDomOrder: true,
  suppressHorizontalScroll: false,
});

// moment.locale("es");

function App() {
  const { updateTheme } = useDarkMode();
  const { isMobile } = useMobile();

  const preventShake = (e: DeviceMotionEvent) => {
    e?.preventDefault?.();
  };

  useEffect(() => {
    window.addEventListener("devicemotion", preventShake);
    return () => window.removeEventListener("devicemotion", preventShake);
  }, []);

  useLayoutEffect(() => {
    updateTheme();
  }, []);

  return (
    <>
      <div
        className={clsx([
          "fixed",
          "inset-0",
          "bg-white",
          "dark:bg-gray-800",
          "z-[-999]",
          "md:hidden",
        ])}
      />
      <LayoutBackgroundFloatingIcons />

      <Toaster
        position={isMobile ? "bottom-center" : "bottom-right"}
        containerStyle={{
          bottom: isMobile ? 110 : 20,
        }}
        reverseOrder
        toastOptions={{
          duration: 3000,
          style: {
            backgroundColor: rgbaObjectToString(TOAST_BG_COLOR),
            color: rgbaObjectToString(TOAST_TEXT_COLOR),
            fontSize: 14,
            fontFamily: "Roboto",
            pointerEvents: "none",
          },
        }}
      />
      <QueryClientProvider client={queryClient}>
        <Router>
          <Routes />
        </Router>
      </QueryClientProvider>
    </>
  );
}

export default App;
